import { useState } from "react";
import usePDA1121_R01Store from "./usePDA1121_R01Store";
import PDA1121_R01Content from "./PDA1121_R01Content";
import { useBarcodeScan } from "src/hooks/useBarcodeScan";

const KeyboardIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
    />
  </svg>
);

const PDA1121_R01Info = () => {
  const { selectedOrder, itemCdInput, setItemCdInput, scanResult } = usePDA1121_R01Store();
  const { handleScan } = PDA1121_R01Content();

  const [keyboardEnabled, setKeyboardEnabled] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);
  const [lastScanned, setLastScanned] = useState("");
  const [isScanning, setIsScanning] = useState(false);

  const handleBarcodeScan = (scannedValue: string) => {
    if (isScanning || lastScanned === scannedValue) return;

    setIsScanning(true);
    setLastScanned(scannedValue);
    handleScan(scannedValue);

    setTimeout(() => {
      setIsScanning(false);
      setLastScanned("");
      scanInputRef.current?.focus();
    }, 300);
  };

  const { scanInputRef, handleScanKeyDown, handleInput } = useBarcodeScan(handleBarcodeScan, {
    clearOnScan: false,
    refocusOnScan: true,
    debounceMs: 100,
    autoFocus: true,
  });

  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTapTime < 300) {
      setKeyboardEnabled(true);
      scanInputRef.current?.focus();
    }
    setLastTapTime(now);
  };

  const toggleKeyboard = () => {
    setKeyboardEnabled(!keyboardEnabled);
    if (!keyboardEnabled) setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const isMatched = scanResult?.matched === true;
  const isMismatched = scanResult?.matched === false;

  return (
    <div className="w-full bg-white p-4 pb-1 border-b border-gray-200 overflow-y-auto">
      {selectedOrder && (
        <div className="space-y-1">
          {/* 공정지시No */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-24 flex-shrink-0">공정지시No</label>
            <input
              type="text"
              value={selectedOrder.productWorkNo || ""}
              readOnly
              className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black"
            />
          </div>

          {/* 품목 */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-24 flex-shrink-0">품목</label>
            <input
              type="text"
              value={selectedOrder.itemNm || ""}
              readOnly
              className="flex-1 px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black"
            />
          </div>

          {/* 자재 Scan */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-24 flex-shrink-0">자재 Scan</label>
            <div className="flex-1 flex gap-1">
              <input
                ref={scanInputRef}
                type="text"
                value={itemCdInput}
                onChange={(e) => setItemCdInput(e.target.value)}
                onInput={handleInput}
                onKeyDown={handleScanKeyDown}
                onClick={handleDoubleTap}
                inputMode={keyboardEnabled ? "text" : "none"}
                autoComplete="off"
                placeholder="품목코드 스캔"
                className={`flex-1 max-w-[197px] px-2 text-lg border rounded text-black focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                  isMatched
                    ? "bg-green-50 border-green-500"
                    : isMismatched
                    ? "bg-red-50 border-red-500"
                    : "bg-white border-gray-300"
                }`}
              />
              <button
                type="button"
                onClick={toggleKeyboard}
                className={`w-10 flex-shrink-0 px-2 border border-gray-300 rounded transition-colors ${
                  keyboardEnabled
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                <KeyboardIcon />
              </button>
            </div>
          </div>

          {/* 자재품명 */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-24 flex-shrink-0">자재품명</label>
            <input
              type="text"
              value={
                scanResult?.matched
                  ? `${scanResult.itemNm}`
                  : ""
              }
              readOnly
              className={`flex-1 px-2 text-lg border rounded text-black transition-colors ${
                isMatched
                  ? "border-green-400 bg-green-50"
                  : "border-gray-300 bg-gray-100"
              }`}
            />
          </div>

          {/* 피더정보 */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-24 flex-shrink-0">피더정보</label>
            <input
              type="text"
              value={scanResult?.matched ? scanResult.feeders.join(", ") : ""}
              readOnly
              className={`flex-1 px-2 text-lg border rounded text-black transition-colors ${
                isMatched
                  ? "border-green-400 bg-green-50"
                  : "border-gray-300 bg-gray-100"
              }`}
            />
          </div>

          {/* 불일치 경고 */}
          {scanResult && !scanResult.matched && (
            <div className="mt-1 rounded-lg p-3 text-center bg-red-50 border-2 border-red-500">
              <p className="text-2xl font-bold text-red-600">❌ 불일치</p>
              <p className="mt-1 text-base text-gray-700">{scanResult.message}</p>
              <p className="text-sm text-gray-500">스캔: {scanResult.itemCd}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PDA1121_R01Info;
